import React from 'react';
import { cn } from '../../utils/cn';

const sizes = {
  sm: 'w-6 h-6 border-[3px]',
  md: 'w-10 h-10 border-4',
  lg: 'w-12 h-12 border-4',
};

const inlineSizes = {
  sm: 'w-4 h-4 border-2',
  md: 'w-5 h-5 border-2',
  lg: 'w-6 h-6 border-[3px]',
};

/**
 * Centered loading affordance — purely presentational.
 * Use `variant="inline"` for compact row placement (e.g. form saving).
 */
export function Loader({
  label = 'Loading',
  className,
  size = 'md',
  fullHeight = true,
  variant = 'centered',
}) {
  if (variant === 'inline') {
    return (
      <span
        className={cn('inline-flex items-center gap-2 text-slate-600', className)}
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <span
          className={cn(
            'inline-block rounded-full animate-spin border-indigo-600 border-t-transparent shrink-0',
            inlineSizes[size] ?? inlineSizes.sm
          )}
          aria-hidden
        />
        {label ? <span className="text-xs font-medium">{label}</span> : null}
        <span className="sr-only">{label || 'Loading'}</span>
      </span>
    );
  }

  const inner = (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className={cn('flex flex-col items-center justify-center gap-3', className)}
    >
      <div
        className={cn(
          'border-indigo-600 border-t-transparent rounded-full animate-spin',
          sizes[size] ?? sizes.md
        )}
      />
      {label ? (
        <p className="text-slate-600 font-medium text-sm text-center max-w-xs">{label}</p>
      ) : null}
      <span className="sr-only">{label || 'Loading'}</span>
    </div>
  );

  if (fullHeight) {
    return (
      <div className="flex items-center justify-center min-h-[240px] sm:min-h-[320px] w-full">
        {inner}
      </div>
    );
  }

  return inner;
}
